import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { PackageOpen, Settings2 } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { useReturnColumns } from '../components/return-columns';
import { returnFilters } from '../components/return-filters';
import { useReturns } from '../hooks/use-returns';

const ROUTE_ID = '/_authed/_active/s/$storeId/returns/' as const;
const ROUTE_PATH = '/s/$storeId/returns/' as const;

function Returns() {
  const { t } = useTranslation('returns');
  const store = useStore();
  const queryClient = useQueryClient();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });

  const filters = useMemo(
    () => returnFilters({ t, queryClient, storeId: store.id }),
    [queryClient, store.id, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useReturns(store.id, list.params);
  const columns = useReturnColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'returns',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={
          <Can permission="settings.read">
            <Button asChild variant="outline" data-tour="returns.list.settings">
              <Link to="/s/$storeId/settings/returns" params={{ storeId: store.id }}>
                <Settings2 aria-hidden />
                {t('settings.open')}
              </Link>
            </Button>
          </Can>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={PackageOpen}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="returns.list.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/returns/$returnId',
              params: { storeId: store.id, returnId: row.id },
            })
          }
          getRowLabel={(row) => row.rmaNumber}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'returns.list.table', search: 'returns.list.search' }}
        />
      )}
    </PageContent>
  );
}

export function ReturnsPage() {
  return (
    <FeatureErrorBoundary name="returns.list">
      <Returns />
    </FeatureErrorBoundary>
  );
}
